import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MemoryFactProto, MemorySource } from "build-ts/memory";
import { deleteMemoryFact, listMemoryFacts, saveMemoryFact } from "services/MemoryFactService";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import AnalystMemoryFacts, { FilterMode } from "../components/AnalystMemoryFacts";

interface AnalystMemoryFactsContainerProps {
  analystId: string | undefined;
  /** When true, triggers a data load even outside the /memory route (e.g. when opened as a modal). */
  isOpen?: boolean;
  // Called whenever a fact is successfully added, edited, toggled, or deleted.
  // Need to refresh the system prompt if the memory facts have been changed.
  onChanged?: () => void;
}

const AnalystMemoryFactsContainer: React.FC<AnalystMemoryFactsContainerProps> = ({ analystId, isOpen, onChanged }) => {
  const pathname = useLocation().pathname;
  const { userId, defaultTimeZone } = useUser();

  // Data state
  const [facts, setFacts] = useState<MemoryFactProto[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // UI state
  const [addText, setAddText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");

  const getAllMemoryFacts = async () => {
    if (!analystId) return;
    setLoading(true);
    try {
      const result = await listMemoryFacts(analystId);
      setFacts(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!pathname.includes("memory") && !isOpen) return;
    getAllMemoryFacts();
  }, [analystId, pathname, isOpen]);

  const handleAdd = async () => {
    if (!analystId || !userId || !addText.trim()) return;
    setAdding(true);
    try {
      const created = await saveMemoryFact(
        analystId,
        userId,
        MemoryFactProto.create({
          text: addText.trim(),
          enabled: true,
          source: MemorySource.MANUAL,
          added_by: userId,
        })
      );
      if (created) {
        setFacts(prev => [created, ...prev]);
        setAddText("");
        showToast("Memory fact added", "", "success");
        onChanged?.();
      }
    } finally {
      setAdding(false);
    }
  };

  const handleToggleEnabled = async (fact: MemoryFactProto) => {
    if (!fact.id || !analystId || !userId) return;
    setTogglingId(fact.id);
    try {
      const updated = await saveMemoryFact(
        analystId,
        userId,
        MemoryFactProto.create({ id: fact.id, enabled: !fact.enabled })
      );
      if (updated) {
        setFacts(prev => prev.map(f => (f.id === fact.id ? updated : f)));
        onChanged?.();
      }
    } finally {
      setTogglingId(null);
    }
  };

  const handleStartEdit = (fact: MemoryFactProto) => {
    setEditingId(fact.id ?? null);
    setEditText(fact.text ?? "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (fact: MemoryFactProto) => {
    if (!userId || !fact.id || !analystId) return;
    if (!editText.trim()) {
      showToast("Text cannot be empty", "", "warning");
      return;
    }
    setSavingId(fact.id);
    try {
      const updated = await saveMemoryFact(
        analystId,
        userId,
        MemoryFactProto.create({
          id: fact.id,
          text: editText.trim(),
          enabled: fact.enabled,
          source: fact.source,
        })
      );
      if (updated) {
        setFacts(prev => prev.map(f => (f.id === fact.id ? updated : f)));
        setEditingId(null);
        setEditText("");
        showToast("Memory fact updated", "", "success");
        onChanged?.();
      }
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (factId: string) => {
    if (!analystId) return;
    setDeletingId(factId);
    try {
      const ok = await deleteMemoryFact(factId);
      if (ok) {
        setFacts(prev => prev.filter(f => f.id !== factId));
        showToast("Memory fact deleted", "", "success");
        onChanged?.();
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (!analystId) return null;

  return (
    <AnalystMemoryFacts
      facts={facts}
      loading={loading}
      adding={adding}
      savingId={savingId}
      deletingId={deletingId}
      togglingId={togglingId}
      defaultTimeZone={defaultTimeZone}
      addText={addText}
      editingId={editingId}
      editText={editText}
      searchText={searchText}
      filterMode={filterMode}
      onAddTextChange={setAddText}
      onEditTextChange={setEditText}
      onSearchTextChange={setSearchText}
      onFilterModeChange={setFilterMode}
      onAdd={handleAdd}
      onToggleEnabled={handleToggleEnabled}
      onStartEdit={handleStartEdit}
      onCancelEdit={handleCancelEdit}
      onSaveEdit={handleSaveEdit}
      onDelete={handleDelete}
    />
  );
};

export default AnalystMemoryFactsContainer;
